import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdScreenShare, MdStopScreenShare, MdVideocam, MdVideocamOff, MdSettings } from 'react-icons/md';
import { AiFillPhone } from 'react-icons/ai';
import { css } from '@emotion/react';
import { useDispatch } from 'react-redux';
import {
  CameraSelection,
  MicSelection,
  SpeakerSelection,
  useMediaStreamMetrics,
  useAttendeeStatus,
  Select,
  FormField,
  useLogger
} from 'amazon-chime-sdk-component-library-react';
import { sendChimeLog } from '@actions';
import { Row, Space, Tooltip, Button, Modal } from 'antd';
import useChimeVideoCall, { Token } from '@hooks/video/useChimeVideoCall';
import { Appointment } from 'vl-common/src/schemas/shared';
import DynamicOnOffMicrophoneIcon from '@components/VideoMenu/DynamicOnOffMicrophoneIcon';
import VideoMenuButton from './VideoMenuButton';
import ParticipantVideoPositions from './ParticipantVideoPositions';
import ChimeSignalStrengthIndicator from './ChimeSignalStrengthIndicator';
import MicrophoneCallout from '@components/VideoMenu/MicrophoneCallout';

interface Video {
  clinician_guid: string;
  patient_guid: string;
  isClinician: boolean;
  token: Token;
  endCall: () => void;
  appointment: Appointment;
}

const Video = ({ clinician_guid, patient_guid, isClinician, token, endCall, appointment }: Video) => {
  const { t } = useTranslation('common');
  const logger = useLogger();
  const {
    leaveMeeting,
    setBackgroundType,
    supportedBackgrounds,
    backgroundType,
    muted,
    toggleMute,
    isVideoEnabled,
    toggleVideo,
    isLocalUserSharing,
    toggleContentShare,
    userChimeIDs,
    hasVideoDevices,
    hasAudioDevices
  } = useChimeVideoCall({
    clinician_guid,
    patient_guid,
    token,
    logger,
    appointment,
    user_guid: isClinician ? clinician_guid : patient_guid
  });
  const dispatch = useDispatch();
  const [showMoreSettings, setShowMoreSettings] = useState(false);
  const [initialisedVideoStreams, setInitialisedVideoStreams] = useState(false);
  const isScreenShareSupported = navigator?.mediaDevices?.getDisplayMedia;
  const metrics = useMediaStreamMetrics();
  const {
    sharingContent: clinicianIsScreensharing,
    signalStrength: clinicianSignalStrength,
    videoEnabled: clinicianVideoEnabled
  } = useAttendeeStatus(userChimeIDs?.clinician);
  const {
    signalStrength: patientSignalStrength,
    videoEnabled: patientVideoEnabled,
    sharingContent: patientIsScreensharing
  } = useAttendeeStatus(userChimeIDs?.patient);
  useEffect(() => {
    const logMetrics = setInterval(
      async (metrics) => {
        if (metrics) {
          await dispatch(
            sendChimeLog({
              timestampMs: new Date().getTime(),
              user_guid: isClinician ? clinician_guid : patient_guid,
              appt_id: appointment.appointment_id,
              message: JSON.stringify(metrics)
            })
          );
        }
      },
      10000,
      metrics
    );
    return () => {
      leaveMeeting();
      clearInterval(logMetrics);
    };
  }, []);

  useEffect(() => {
    if (patientVideoEnabled || clinicianVideoEnabled) setInitialisedVideoStreams(true);
  }, [patientVideoEnabled, clinicianVideoEnabled]);

  const hangupRequested = async () => {
    leaveMeeting(endCall);
  };

  return (
    <>
      <Row>
        {(isClinician ? !!userChimeIDs?.patient : !!userChimeIDs?.clinician) && (
          <Row align="bottom" style={{ marginBottom: 16 }}>
            {isClinician ? appointment?.patient : appointment?.clinician}
            <ChimeSignalStrengthIndicator
              style={{ transform: 'translateY(2px)', marginLeft: 5 }}
              strength={isClinician ? patientSignalStrength : clinicianSignalStrength}
            />
          </Row>
        )}
      </Row>
      <div style={{ position: 'relative' }}>
        <div style={{ width: '100%', position: 'relative', minHeight: 320 }}>
          <ParticipantVideoPositions
            initialisedVideoStreams={initialisedVideoStreams}
            isClinician={isClinician}
            clinicianIsScreensharing={clinicianIsScreensharing}
            patientIsScreensharing={patientIsScreensharing}
            userChimeIDs={userChimeIDs}
          />
        </div>
        <Row style={{ display: initialisedVideoStreams ? undefined : 'none', width: '100%', marginTop: 10 }}>
          <Space size="small" align="start">
            <MicrophoneCallout>
              <VideoMenuButton
                data-testid="toggle-mute"
                title={muted ? 'Start Audio (m)' : 'Mute Audio (m)'}
                disabled={!hasAudioDevices}
                icon={<DynamicOnOffMicrophoneIcon isAudioEnabled={!muted} />}
                onClick={toggleMute}
                pulsateOnCallStart
              />
            </MicrophoneCallout>
            <VideoMenuButton
              data-testid="toggle-video"
              title={isVideoEnabled ? 'Start Video (v)' : 'Stop Video (v)'}
              disabled={!hasVideoDevices}
              onClick={toggleVideo}
              icon={isVideoEnabled ? <MdVideocam /> : <MdVideocamOff />}
            />
            <VideoMenuButton
              data-testid="toggle-screenshare"
              title={isLocalUserSharing ? 'Stop Screensharing (s)' : 'Start Screensharing (s)'}
              disabled={!isScreenShareSupported}
              onClick={toggleContentShare}
              icon={isLocalUserSharing ? <MdStopScreenShare /> : <MdScreenShare />}
            />
            <VideoMenuButton
              data-testid="device-selection"
              title="Call settings (e.g. device select)"
              onClick={() => {
                setShowMoreSettings(!showMoreSettings);
              }}
              icon={<MdSettings />}
            />
            <ChimeSignalStrengthIndicator
              strength={isClinician ? clinicianSignalStrength : patientSignalStrength}
              metrics={metrics}
            />
            <Tooltip title="Hang up (Esc)" trigger={['hover', 'focus']}>
              <Button
                aria-label={t('Hang up')}
                shape="circle"
                type="primary"
                danger
                data-testid="hang-up"
                css={css`
                  font-size: 20px;
                  margin: 0;
                  padding: 0;
                `}
                icon={<AiFillPhone />}
                onClick={hangupRequested}
              />
            </Tooltip>
          </Space>
        </Row>
        <Modal
          centered
          open={showMoreSettings}
          closable={false}
          title="Change audio and video input devices"
          onOk={() => setShowMoreSettings(false)}
          cancelButtonProps={{ style: { display: 'none' } }}
        >
          <CameraSelection notFoundMsg="No cameras found" label={t('Video Device')} />
          <MicSelection notFoundMsg="No microphones found" label={t('Audio Device')} />
          <SpeakerSelection notFoundMsg="No speakers found" />
          <FormField
            field={Select}
            options={Object.keys(supportedBackgrounds)?.map((key) => ({
              value: key,
              label: key,
              disabled: supportedBackgrounds[key]
            }))}
            onChange={(e) => setBackgroundType((e?.target as HTMLButtonElement)?.value || 'none')}
            value={backgroundType}
            label={t('Background Replacement')}
          />
        </Modal>
      </div>
    </>
  );
};

export default Video;
